import { jwtDecode } from 'jwt-decode';

/**
 * Decode JWT token to extract payload and expiration
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded token payload or null if invalid
 */
export function decodeToken(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const decoded = jwtDecode(token);
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/** Stable user id from a JWT (`sub` or `user_id`). */
export function authUserIdFromToken(token) {
  const decoded = decodeToken(token);
  if (!decoded) return null;
  const id = decoded.sub ?? decoded.user_id;
  if (id == null || id === "") return null;
  return String(id);
}

/**
 * Whether React auth state should update. Same-user token rotation is silent.
 */
export function shouldPublishAuthToken(prevToken, nextToken) {
  const prevOn = Boolean(prevToken);
  const nextOn = Boolean(nextToken);
  if (prevOn !== nextOn) return true;
  return authUserIdFromToken(prevToken) !== authUserIdFromToken(nextToken);
}

/**
 * Get token expiration time in milliseconds
 * @param {string} token - JWT token string
 * @returns {number|null} Expiration timestamp in milliseconds or null if invalid
 */
export function getTokenExpirationTime(token) {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  // exp is in seconds, convert to milliseconds
  return decoded.exp * 1000;
}

/**
 * Check if token is expiring soon (within buffer time)
 * @param {string} token - JWT token string
 * @param {number} bufferMinutes - Buffer time in minutes (default: 2)
 * @returns {boolean} True if token expires within buffer time
 */
export function isTokenExpiringSoon(token, bufferMinutes = 2) {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) {
    return true; // Treat invalid token as expiring soon
  }

  const currentTime = Date.now();
  const bufferMs = bufferMinutes * 60 * 1000;
  const timeUntilExpiration = expirationTime - currentTime;

  return timeUntilExpiration <= bufferMs;
}

/**
 * Check if token is valid (exists and not expired)
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is valid and not expired
 */
export function isTokenValid(token) {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) {
    return false;
  }

  const currentTime = Date.now();
  return currentTime < expirationTime;
}

/**
 * Calculate milliseconds until token should be refreshed
 * @param {string} token - JWT token string
 * @param {number} bufferMinutes - Buffer time in minutes (default: 2)
 * @returns {number|null} Milliseconds until refresh should occur, or null if invalid/expired
 */
export function getTimeUntilRefresh(token, bufferMinutes = 2) {
  const expirationTime = getTokenExpirationTime(token);
  if (!expirationTime) {
    return null;
  }

  const currentTime = Date.now();
  const bufferMs = bufferMinutes * 60 * 1000;
  const timeUntilExpiration = expirationTime - currentTime;
  const timeUntilRefresh = timeUntilExpiration - bufferMs;

  // If token expires very soon (less than buffer time), refresh immediately
  return Math.max(0, timeUntilRefresh);
}

